// ============================================================
// ORGANIZATION SELECTOR - For Super Admins
// File: src/components/OrganizationSelector.js
// Allows super admins to select which organization to work with
// ============================================================

import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { Card } from './UI'
import { useTheme } from '../ThemeContext'

export default function OrganizationSelector({ onSelectOrganization }) {
  const { colors, spacing, typography, radius } = useTheme()
  const [organizations, setOrganizations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchOrganizations()
  }, [])

  async function fetchOrganizations() {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('active', true)
        .order('name')

      if (error) throw error
      setOrganizations(data || [])
    } catch (err) {
      console.error('Error fetching organizations:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: colors.bg.primary
      }}>
        <Card padding="xl" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: typography.fontSizes['2xl'], marginBottom: spacing.lg }}>
            Loading organizations...
          </div>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: colors.bg.primary
      }}>
        <Card padding="xl" style={{ textAlign: 'center' }}>
          <div style={{ color: colors.danger, marginBottom: spacing.md }}>
            ⚠️ Error loading organizations
          </div>
          <div style={{ color: colors.text.secondary, fontSize: typography.fontSizes.sm }}>
            {error}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: colors.bg.primary,
      padding: spacing.lg
    }}>
      <Card padding="xl" style={{ maxWidth: '600px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: spacing.xl }}>
          <h1 style={{
            margin: 0,
            marginBottom: spacing.sm,
            fontSize: typography.fontSizes['3xl'],
            fontWeight: typography.fontWeights.bold,
            color: colors.text.primary
          }}>
            Select Organization
          </h1>
          <p style={{
            margin: 0,
            color: colors.text.secondary,
            fontSize: typography.fontSizes.sm
          }}>
            Choose which organization you want to work with
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {organizations.map(org => (
            <button
              key={org.id}
              onClick={() => onSelectOrganization(org)}
              style={{
                padding: spacing.lg,
                backgroundColor: colors.bg.secondary,
                border: `2px solid ${colors.border.primary}`,
                borderRadius: radius.lg,
                cursor: 'pointer',
                transition: 'all 0.2s',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.primary
                e.currentTarget.style.backgroundColor = colors.bg.tertiary
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border.primary
                e.currentTarget.style.backgroundColor = colors.bg.secondary
              }}
            >
              <div style={{
                fontSize: typography.fontSizes.lg,
                fontWeight: typography.fontWeights.semibold,
                color: colors.text.primary,
                marginBottom: spacing.xs
              }}>
                {org.name}
              </div>
              <div style={{
                fontSize: typography.fontSizes.sm,
                color: colors.text.tertiary
              }}>
                {org.slug}
              </div>
            </button>
          ))}
        </div>

        {organizations.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: spacing.xl,
            color: colors.text.tertiary,
            fontSize: typography.fontSizes.sm
          }}>
            No organizations found
          </div>
        )}
      </Card>
    </div>
  )
}
